import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { keyframes } from "styled-components";
import { Modal, IconButton, CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import { CloudDoneRounded } from "@material-ui/icons";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { TIFFViewer } from "react-tiff";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const DOCX_TO_PDF_URL = "https://staging-api.justact.co.in/pdf_convert";

const EMPTY_MATCHES = [];

const appendCacheBuster = (url, timestamp) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}_=${timestamp}` : "";

const yieldToMain = () =>
  new Promise((resolve) => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => resolve(), { timeout: 50 });
    } else {
      window.setTimeout(resolve, 0);
    }
  });

const countWords = (text) => {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
};

const extractPageTextData = async (page) => {
  const textContent = await page.getTextContent();
  const items = textContent.items;
  let text = "";
  const offsets = new Array(items.length);

  for (let i = 0; i < items.length; i += 1) {
    const str = items[i].str || "";
    offsets[i] = { start: text.length, length: str.length };
    text += str;
    if (items[i].hasEOL) {
      text += "\n";
    } else if (str && i < items.length - 1 && !/\s$/.test(str)) {
      text += " ";
    }
  }

  return { text, offsets, wordCount: countWords(text) };
};

const mapMatchToItemRanges = (offsets, matchStart, matchEnd) => {
  const pieces = [];
  for (let i = 0; i < offsets.length; i += 1) {
    const { start, length } = offsets[i];
    const itemStart = start;
    const itemEnd = start + length;
    if (itemStart >= matchEnd) break;
    const overlapStart = Math.max(itemStart, matchStart);
    const overlapEnd = Math.min(itemEnd, matchEnd);
    if (overlapStart < overlapEnd) {
      pieces.push({
        itemIndex: i,
        startOffsetInItem: overlapStart - itemStart,
        endOffsetInItem: overlapEnd - itemStart,
      });
    }
  }
  return pieces;
};

const searchDocument = async (
  pdf,
  searchTerm,
  getCachedPageText,
  isCancelled,
) => {
  const trimmed = searchTerm.trim();
  if (!pdf || !trimmed) return [];

  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const expression = new RegExp(escaped, "gi");
  const results = [];
  let lastYield = performance.now();

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    if (isCancelled()) return [];

    const { text } = await getCachedPageText(pdf, pageNumber);
    expression.lastIndex = 0;
    let match;
    let pageMatchIndex = 0;
    while ((match = expression.exec(text)) !== null) {
      results.push({
        pageNumber,
        pageMatchIndex,
        start: match.index,
        end: match.index + match[0].length,
      });
      pageMatchIndex += 1;
      if (match[0].length === 0) expression.lastIndex += 1;
    }

    if (performance.now() - lastYield > 12) {
      await yieldToMain();
      lastYield = performance.now();
      if (isCancelled()) return [];
    }
  }

  return results;
};

const PdfPage = ({
  pdf,
  pageNumber,
  zoom,
  pageMatches,
  activePageMatchIndex,
  getCachedPageText,
}) => {
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const textDivsRef = useRef([]);
  const [ready, setReady] = useState(false);
  const [offsets, setOffsets] = useState(null);
  const [matchRects, setMatchRects] = useState([]);

  useEffect(() => {
    if (!pdf || !canvasRef.current || !textLayerRef.current) return undefined;
    let cancelled = false;
    let renderTask;
    setReady(false);

    const renderPage = async () => {
      const page = await pdf.getPage(pageNumber);
      if (cancelled) return;

      const viewport = page.getViewport({ scale: zoom });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      const pixelRatio = window.devicePixelRatio || 1;

      canvas.width = Math.floor(viewport.width * pixelRatio);
      canvas.height = Math.floor(viewport.height * pixelRatio);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      const textLayer = textLayerRef.current;
      textLayer.replaceChildren();
      textLayer.style.width = `${viewport.width}px`;
      textLayer.style.height = `${viewport.height}px`;

      renderTask = page.render({ canvasContext: context, viewport });
      await renderTask.promise;
      if (cancelled) return;

      const textContent = await page.getTextContent();
      const divs = [];
      await pdfjsLib.renderTextLayer({
        textContent,
        container: textLayer,
        viewport,
        textDivs: divs,
      }).promise;
      if (cancelled) return;
      textDivsRef.current = divs;

      const data = await getCachedPageText(pdf, pageNumber);
      if (cancelled) return;
      setOffsets(data.offsets);
      setReady(true);
    };

    renderPage().catch(() => {});
    return () => {
      cancelled = true;
      setReady(false);
      if (renderTask) renderTask.cancel();
    };
  }, [pageNumber, pdf, zoom, getCachedPageText]);

  useEffect(() => {
    if (!ready || !offsets || !pageMatches.length) {
      setMatchRects([]);
      return;
    }
    const container = textLayerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const rects = [];

    pageMatches.forEach((match) => {
      const pieces = mapMatchToItemRanges(offsets, match.start, match.end);
      pieces.forEach(({ itemIndex, startOffsetInItem, endOffsetInItem }) => {
        const div = textDivsRef.current[itemIndex];
        const textNode = div && div.firstChild;
        if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;

        const maxLen = textNode.nodeValue.length;
        const range = document.createRange();
        try {
          range.setStart(textNode, Math.min(startOffsetInItem, maxLen));
          range.setEnd(textNode, Math.min(endOffsetInItem, maxLen));
        } catch (error) {
          return;
        }

        Array.from(range.getClientRects()).forEach((rect) => {
          if (rect.width <= 0 || rect.height <= 0) return;
          rects.push({
            isActive: match.pageMatchIndex === activePageMatchIndex,
            top: rect.top - containerRect.top,
            left: rect.left - containerRect.left,
            width: rect.width,
            height: rect.height,
          });
        });
      });
    });

    setMatchRects(rects);
  }, [ready, offsets, pageMatches, activePageMatchIndex]);

  return (
    <PdfPaper>
      <canvas ref={canvasRef} />
      <PdfTextLayer ref={textLayerRef} />
      <HighlightLayer aria-hidden="true">
        {matchRects.map((rect, index) => (
          <HighlightMark
            key={index}
            $active={rect.isActive}
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            }}
          />
        ))}
      </HighlightLayer>
    </PdfPaper>
  );
};

const PdfPageSlot = ({
  pdf,
  pageNumber,
  zoom,
  matches,
  activeResult,
  forceRender,
  pageRef,
  getCachedPageText,
}) => {
  const slotRef = useRef(null);
  const [isVisible, setIsVisible] = useState(forceRender);

  useEffect(() => {
    if (forceRender) setIsVisible(true);
  }, [forceRender]);

  useEffect(() => {
    const element = slotRef.current;
    if (!element || !window.IntersectionObserver) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting || forceRender),
      { rootMargin: "900px 0px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [forceRender]);

  const pageMatches = matches.get(pageNumber) || EMPTY_MATCHES;

  return (
    <PdfPageSlotWrap
      ref={(element) => {
        slotRef.current = element;
        pageRef(element);
      }}
      $height={Math.round(842 * zoom)}
    >
      {isVisible ? (
        <PdfPage
          pdf={pdf}
          pageNumber={pageNumber}
          zoom={zoom}
          pageMatches={pageMatches}
          activePageMatchIndex={
            activeResult?.pageNumber === pageNumber
              ? activeResult.pageMatchIndex
              : -1
          }
          getCachedPageText={getCachedPageText}
        />
      ) : (
        <PdfPagePlaceholder>Page {pageNumber}</PdfPagePlaceholder>
      )}
    </PdfPageSlotWrap>
  );
};

const PdfPreview = ({
  pdfUrl,
  searchTerm,
  activeResult,
  onSearchResults,
  onSearchingChange,
}) => {
  const [pdf, setPdf] = useState(null);
  const [zoom, setZoom] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [wordCount, setWordCount] = useState(null);
  const [results, setResults] = useState([]);
  const pageRefs = useRef({});

  const pageTextCacheRef = useRef(new Map());
  const pendingRef = useRef(new Map());

  const getCachedPageText = useCallback((pdfDoc, pageNumber) => {
    const cache = pageTextCacheRef.current;
    if (cache.has(pageNumber)) return Promise.resolve(cache.get(pageNumber));

    const pending = pendingRef.current;
    if (pending.has(pageNumber)) return pending.get(pageNumber);

    const promise = pdfDoc
      .getPage(pageNumber)
      .then((page) => extractPageTextData(page))
      .then((data) => {
        cache.set(pageNumber, data);
        pending.delete(pageNumber);
        return data;
      })
      .catch((err) => {
        pending.delete(pageNumber);
        throw err;
      });

    pending.set(pageNumber, promise);
    return promise;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loadingTask;
    setPdf(null);
    setError("");
    setLoading(true);
    setWordCount(null);
    setResults([]);
    pageTextCacheRef.current = new Map();
    pendingRef.current = new Map();

    loadingTask = pdfjsLib.getDocument({ url: pdfUrl });
    loadingTask.promise
      .then((document) => {
        if (!cancelled) setPdf(document);
      })
      .catch(() => {
        if (!cancelled) setError("The converted PDF could not be displayed.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (loadingTask) loadingTask.destroy();
    };
  }, [pdfUrl]);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSearchTerm(searchTerm.trim()),
      250,
    );
    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (!pdf || !debouncedSearchTerm) {
      setResults([]);
      onSearchResults([]);
      onSearchingChange(false);
      return undefined;
    }

    let cancelled = false;
    onSearchingChange(true);
    searchDocument(pdf, debouncedSearchTerm, getCachedPageText, () => cancelled)
      .then((found) => {
        if (cancelled) return;
        setResults(found);
        onSearchResults(found);
        onSearchingChange(false);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        onSearchResults([]);
        onSearchingChange(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    debouncedSearchTerm,
    getCachedPageText,
    onSearchResults,
    onSearchingChange,
    pdf,
  ]);

  useEffect(() => {
    if (!pdf) {
      setWordCount(null);
      return undefined;
    }

    let cancelled = false;
    setWordCount({ value: 0, done: false });

    const run = async () => {
      let total = 0;
      let lastYield = performance.now();

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        if (cancelled) return;
        const data = await getCachedPageText(pdf, pageNumber);
        total += data.wordCount;

        if (pageNumber % 10 === 0 || pageNumber === pdf.numPages) {
          if (!cancelled) {
            setWordCount({
              value: total,
              done: pageNumber === pdf.numPages,
            });
          }
        }

        if (performance.now() - lastYield > 12) {
          await yieldToMain();
          lastYield = performance.now();
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [pdf, getCachedPageText]);

  useEffect(() => {
    if (!activeResult?.pageNumber) return;
    const page = pageRefs.current[activeResult.pageNumber];
    if (page) page.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeResult]);

  const matchesByPage = useMemo(() => {
    const map = new Map();
    results.forEach((match) => {
      if (!map.has(match.pageNumber)) map.set(match.pageNumber, []);
      map.get(match.pageNumber).push(match);
    });
    return map;
  }, [results]);

  if (loading)
    return <CustomLoading label="Preparing a high-quality preview…" />;
  if (error) return <PreviewError>{error}</PreviewError>;
  if (!pdf) return null;

  return (
    <PdfPreviewWrap>
      <PdfToolbar>
        <ContinuousHint>
          {pdf.numPages.toLocaleString()} pages · scroll to read
          {wordCount
            ? ` · ${wordCount.value.toLocaleString()}${
                wordCount.done ? "" : "+"
              } words`
            : ""}
        </ContinuousHint>
        <ToolGroup>
          <ToolButton
            title="Zoom out"
            disabled={zoom <= 0.65}
            onClick={() => setZoom((current) => Math.max(0.65, current - 0.15))}
          >
            <ZoomOutIcon fontSize="small" />
          </ToolButton>
          <ZoomLabel>{Math.round(zoom * 100)}%</ZoomLabel>
          <ToolButton
            title="Zoom in"
            disabled={zoom >= 2.2}
            onClick={() => setZoom((current) => Math.min(2.2, current + 0.15))}
          >
            <ZoomInIcon fontSize="small" />
          </ToolButton>
        </ToolGroup>
      </PdfToolbar>
      <PdfScroller>
        {Array.from({ length: pdf.numPages }, (_, index) => index + 1).map(
          (pageNumber) => (
            <PdfPageSlot
              key={pageNumber}
              pdf={pdf}
              pageNumber={pageNumber}
              zoom={zoom}
              matches={matchesByPage}
              activeResult={activeResult}
              forceRender={activeResult?.pageNumber === pageNumber}
              pageRef={(element) => {
                pageRefs.current[pageNumber] = element;
              }}
              getCachedPageText={getCachedPageText}
            />
          ),
        )}
      </PdfScroller>
    </PdfPreviewWrap>
  );
};

const VideoViewer = ({ url, name }) => (
  <MediaViewerWrap>
    <VideoCard>
      <video
        src={url}
        controls
        style={{ width: "100%", maxHeight: "65vh", borderRadius: 8 }}
      />
      <MediaTitle>{name}</MediaTitle>
    </VideoCard>
  </MediaViewerWrap>
);

const AudioViewer = ({ url, name }) => (
  <MediaViewerWrap>
    <AudioCard>
      <AudioDisc>{"🎵"}</AudioDisc>
      <AudioTitle>{name}</AudioTitle>
      <audio src={url} controls style={{ width: "100%", maxWidth: 340 }} />
    </AudioCard>
  </MediaViewerWrap>
);

const DocViewers = ({ modal, setModal }) => {
  const [previewTimestamp] = useState(() => Date.now());
  const [convertedPdfUrl, setConvertedPdfUrl] = useState("");
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  const fileUrl = modal?.url || "";
  const fileName = modal?.name || "Document";
  const ext =
    modal?.format?.toLowerCase() ||
    fileUrl.split(".").pop()?.split("?")[0]?.toLowerCase() ||
    "";
  const safeUrl = appendCacheBuster(fileUrl, previewTimestamp);
  const isDocx = ext === "docx";
  const isPdf = ext === "pdf";
  const isTiff = ["tif", "tiff"].includes(ext);
  const isVideo = ["mp4", "mkv", "mov", "webm"].includes(ext);
  const isAudio = ["mp3", "wav", "ogg", "m4a"].includes(ext);
  const isMSOffice = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(
    ext,
  );
  const pdfPreviewUrl = isDocx ? convertedPdfUrl : isPdf ? safeUrl : "";

  useEffect(() => {
    setSearchTerm("");
    setSearchResults([]);
    setActiveResultIndex(0);
    setIsSearching(false);
  }, [fileUrl]);

  useEffect(() => {
    if (!isDocx || !fileUrl || !modal?.state) {
      setConvertedPdfUrl("");
      setConversionLoading(false);
      setConversionError("");
      return undefined;
    }

    let cancelled = false;
    setConvertedPdfUrl("");
    setConversionLoading(true);
    setConversionError("");

    fetch(DOCX_TO_PDF_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ documents: [{ url: fileUrl }] }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data?.url) throw new Error("Conversion failed");
        return data.url;
      })
      .then((url) => {
        if (!cancelled)
          setConvertedPdfUrl(appendCacheBuster(url, previewTimestamp));
      })
      .catch(() => {
        if (!cancelled)
          setConversionError(
            "We could not convert this DOCX file to a preview.",
          );
      })
      .finally(() => {
        if (!cancelled) setConversionLoading(false);
      });

    return () => {
      cancelled = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUrl, isDocx, modal?.state, previewTimestamp]);

  const handleSearchResults = useCallback((results) => {
    setSearchResults(results);
    setActiveResultIndex(0);
  }, []);

  const moveToResult = useCallback((direction) => {
    setSearchResults((currentResults) => {
      if (!currentResults.length) return currentResults;
      setActiveResultIndex(
        (current) =>
          (current + direction + currentResults.length) % currentResults.length,
      );
      return currentResults;
    });
  }, []);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      moveToResult(event.shiftKey ? -1 : 1);
    } else if (event.key === "Escape") {
      setSearchTerm("");
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!modal?.state || !fileUrl) return null;

  const activeResult = searchResults.length
    ? { ...searchResults[activeResultIndex], index: activeResultIndex }
    : null;
  const canSearch = isDocx || isPdf;
  const documents = [{ uri: safeUrl, fileName, fileType: ext || undefined }];

  return (
    <Modal open={modal.state} onClose={() => setModal({ state: false })}>
      <PreviewContainer>
        <Header>
          <FileInfo>
            <FileName title={fileName}>{fileName}</FileName>
            <FileFormat>{ext.toUpperCase()}</FileFormat>
          </FileInfo>
          {canSearch && (
            <SearchControls>
              <SearchIcon fontSize="small" />
              <SearchInput
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search document"
                aria-label="Search document"
              />
              {searchTerm.trim() && (
                <SearchCount>
                  {isSearching
                    ? "Searching…"
                    : searchResults.length
                      ? `${activeResultIndex + 1} / ${searchResults.length}`
                      : "No results"}
                </SearchCount>
              )}
              <SearchNavButton
                title="Previous match (Shift+Enter)"
                disabled={!searchResults.length}
                onClick={() => moveToResult(-1)}
              >
                <NavigateBeforeIcon fontSize="small" />
              </SearchNavButton>
              <SearchNavButton
                title="Next match (Enter)"
                disabled={!searchResults.length}
                onClick={() => moveToResult(1)}
              >
                <NavigateNextIcon fontSize="small" />
              </SearchNavButton>
            </SearchControls>
          )}
          <HeaderActions>
            <HeaderButton
              onClick={handleDownload}
              title={`Download original ${ext.toUpperCase()} file`}
            >
              <CloudDoneRounded />
            </HeaderButton>
            <CloseButton
              onClick={() => setModal({ state: false })}
              title="Close preview"
            >
              <CloseIcon />
            </CloseButton>
          </HeaderActions>
        </Header>

        <ViewerWrapper>
          {modal.headerImage && (
            <LetterheadImage
              src={modal.headerImage}
              alt="Selected letterhead header"
              $position="top"
            />
          )}
          <PreviewBody>
            {conversionLoading ? (
              <CustomLoading label="loading DOCX…" />
            ) : conversionError ? (
              <PreviewError>{conversionError}</PreviewError>
            ) : pdfPreviewUrl ? (
              <PdfPreview
                pdfUrl={pdfPreviewUrl}
                searchTerm={searchTerm}
                activeResult={activeResult}
                onSearchResults={handleSearchResults}
                onSearchingChange={setIsSearching}
              />
            ) : isTiff ? (
              <TiffWrapper>
                <TIFFViewer
                  tiff={safeUrl}
                  paginate="bottom"
                  zoomable
                  printable={false}
                  style={{ width: "100%", height: "100%" }}
                />
              </TiffWrapper>
            ) : isVideo ? (
              <VideoViewer url={safeUrl} name={fileName} />
            ) : isAudio ? (
              <AudioViewer url={safeUrl} name={fileName} />
            ) : (
              <DocViewer
                documents={documents}
                pluginRenderers={DocViewerRenderers}
                config={{
                  header: { disableHeader: true, disableFileName: true },
                  loadingRenderer: { overrideComponent: CustomLoading },
                  csvDelimiter: ",",
                }}
                style={{ width: "100%", height: "100%", background: "white" }}
              />
            )}
            {isMSOffice && !isDocx && <FooterBlocker />}
          </PreviewBody>
          {modal.footerImage && (
            <LetterheadImage
              src={modal.footerImage}
              alt="Selected letterhead footer"
              $position="bottom"
            />
          )}
        </ViewerWrapper>
      </PreviewContainer>
    </Modal>
  );
};

const CustomLoading = ({ label = "Loading document…" }) => (
  <LoadingOverlay>
    <CircularProgress size={52} style={{ color: "#00a9a5" }} />
    <LoadingText>{label}</LoadingText>
  </LoadingOverlay>
);

export default DocViewers;

const PreviewContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(94vw, 1260px);
  height: min(92vh, 920px);
  background: #f4f7f8;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 28px 90px rgba(2, 43, 50, 0.36);
  display: flex;
  flex-direction: column;
  outline: none;
`;
const Header = styled.div`
  min-height: 76px;
  padding: 12px 18px 12px 24px;
  color: white;
  background: #00838c;
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;
`;
const FileInfo = styled.div`
  flex: 1;
  min-width: 110px;
`;
const FileName = styled.div`
  font-size: 17px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const FileFormat = styled.span`
  display: block;
  margin-top: 3px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  opacity: 0.82;
`;
const HeaderActions = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;
const HeaderButton = styled(IconButton)`
  color: #fff !important;
  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
`;
const CloseButton = styled(HeaderButton)`
  &:hover {
    background: rgba(0, 0, 0, 0.13);
  }
`;
const SearchControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: min(390px, 40vw);
  min-width: 220px;
  padding: 5px 7px 5px 10px;
  color: #efffff;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.36);
  border-radius: 8px;
  @media (max-width: 680px) {
    display: none;
  }
`;
const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  color: white;
  background: transparent;
  font-size: 13px;
  &::placeholder {
    color: rgba(255, 255, 255, 0.78);
  }
`;
const SearchCount = styled.span`
  white-space: nowrap;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 600;
`;
const SearchNavButton = styled.button`
  display: grid;
  place-items: center;
  width: 25px;
  height: 25px;
  padding: 0;
  border: 0;
  border-radius: 5px;
  color: white;
  background: transparent;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;
const ViewerWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  background: #edf2f3;
`;
const PreviewBody = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;
const LetterheadImage = styled.img`
  width: 100%;
  max-height: 20%;
  object-fit: contain;
  object-position: ${({ $position }) => $position};
  flex-shrink: 0;
  background: white;
`;
const PdfPreviewWrap = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;
const PdfToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 48px;
  padding: 0 18px;
  background: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid #dfe8e9;
  box-shadow: 0 2px 8px rgba(16, 54, 59, 0.06);
  z-index: 2;
`;
const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;
const ToolButton = styled.button`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 1px solid #dde6e7;
  border-radius: 7px;
  color: #16626a;
  background: white;
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: #76c9ca;
    background: #ecfbfb;
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;
const ContinuousHint = styled.span`
  color: #456066;
  font-size: 13px;
  font-weight: 600;
`;
const ZoomLabel = styled.span`
  min-width: 42px;
  color: #456066;
  font-size: 12px;
  text-align: center;
`;
const PdfScroller = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  gap: 22px;
  overflow: auto;
  padding: 28px;
  background: linear-gradient(135deg, #e6edef, #f4f7f8);
`;
const PdfPageSlotWrap = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  min-height: ${({ $height }) => $height}px;
`;
const PdfPagePlaceholder = styled.div`
  display: grid;
  width: min(595px, 100%);
  height: 100%;
  min-height: inherit;
  place-items: center;
  color: #9aabad;
  font-size: 13px;
  background: #fff;
  box-shadow: 0 6px 25px rgba(12, 42, 47, 0.08);
`;
const PdfPaper = styled.div`
  position: relative;
  align-self: flex-start;
  background: white;
  box-shadow: 0 6px 25px rgba(12, 42, 47, 0.18);
  canvas {
    display: block;
  }
`;
const PdfTextLayer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  line-height: 1;
  text-align: initial;
  span,
  br {
    position: absolute;
    color: transparent;
    white-space: pre;
    cursor: text;
    transform-origin: 0 0;
  }
`;
/* Highlights are drawn as a purely visual overlay, on top of (but separate
 * from) the text layer. The text layer's own spans/text-nodes are never
 * mutated, so native text selection and copy/paste keep working exactly as
 * pdf.js intended. */
const HighlightLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;
const HighlightMark = styled.div`
  position: absolute;
  border-radius: 2px;
  background: ${({ $active }) =>
    $active ? "rgba(255, 143, 0, 0.55)" : "rgba(255, 224, 61, 0.55)"};
  box-shadow: ${({ $active }) =>
    $active ? "0 0 0 2px rgba(255, 143, 0, 0.5)" : "none"};
`;
const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: rgba(255, 255, 255, 0.92);
`;
const LoadingText = styled.p`
  margin: 0;
  color: #426166;
  font-size: 14px;
  font-weight: 600;
`;
const PreviewError = styled.div`
  display: grid;
  place-items: center;
  flex: 1;
  padding: 28px;
  color: #a63e35;
  font-size: 14px;
  text-align: center;
  background: #fffafa;
`;
const TiffWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px;
  overflow: auto;
  background: white;
`;
const FooterBlocker = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50px;
  background: white;
  z-index: 10;
`;
const MediaViewerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
  background: #f1f5f9;
`;
const VideoCard = styled.div`
  width: min(100%, 800px);
  padding: 16px;
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
`;
const MediaTitle = styled.div`
  margin-top: 12px;
  overflow: hidden;
  color: #f1f5f9;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const AudioCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: min(100%, 420px);
  padding: 36px;
  text-align: center;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
`;
const pulse = keyframes`0%,100% { transform: scale(.96); } 50% { transform: scale(1.04); }`;
const AudioDisc = styled.div`
  display: grid;
  width: 90px;
  height: 90px;
  place-items: center;
  color: white;
  font-size: 32px;
  background: linear-gradient(135deg, #00838c, #00606a);
  border-radius: 50%;
  animation: ${pulse} 3s ease-in-out infinite;
`;
const AudioTitle = styled.div`
  max-width: 100%;
  overflow: hidden;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
